
import { Route, Routes as RouterRoutes, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { Suspense, lazy, ReactNode, useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Lazy loaded pages for better performance
const Index = lazy(() => import("@/pages/Index"));
const About = lazy(() => import("@/pages/About"));
const Auth = lazy(() => import("@/pages/Auth"));
const App = lazy(() => import("@/pages/App"));
const Chat = lazy(() => import("@/pages/Chat"));
const MathGame = lazy(() => import("@/pages/MathGame"));
const MathGamePlay = lazy(() => import("@/pages/MathGamePlay"));
const MathLab = lazy(() => import("@/pages/MathLab"));
const Art = lazy(() => import("@/pages/Art"));
const Journal = lazy(() => import("@/pages/Journal"));
const Stories = lazy(() => import("@/pages/Stories"));
const Wordie = lazy(() => import("@/pages/Wordie"));
const MemoryGame = lazy(() => import("@/pages/MemoryGame"));
const TypingGame = lazy(() => import("@/pages/TypingGame"));
const Science = lazy(() => import("@/pages/Science"));
const Simulator = lazy(() => import("@/pages/Simulator"));
const DictionaryRedirect = lazy(() => import("@/pages/DictionaryRedirect"));
const Vocabulary = lazy(() => import("@/pages/Vocabulary"));
const Language = lazy(() => import("@/pages/Language"));
const Achievements = lazy(() => import("@/pages/Achievements"));
const Account = lazy(() => import("@/pages/Account"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Help = lazy(() => import("@/pages/Help"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-12 w-12 animate-spin text-koala-primary" />
  </div>
);

// Protected route component with redirect protection
interface ProtectedRouteProps {
  children: ReactNode;
  requiresAuth?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  requiresAuth = true
}: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  // Set hasChecked to true once auth check is complete
  useEffect(() => {
    if (!isLoading) {
      setHasChecked(true);
    }
  }, [isLoading]);

  // If we're still checking auth status, show loading
  if (isLoading) {
    return <PageLoader />;
  }

  // If this route requires authentication and user is not logged in
  if (requiresAuth && !user && hasChecked) {
    console.log("Protected route access denied - not authenticated, redirecting to /auth");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }


  // If all checks pass, render the children
  return <>{children}</>;
};

const Routes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<Index />} />
        <Route path="/about" element={<About />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/help" element={<Help />} />
        
        {/* Protected routes */}
        <Route path="/app" element={
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        } />
        
        <Route path="/chat" element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } />
        
        <Route path="/math-game" element={
          <ProtectedRoute>
            <MathGame />
          </ProtectedRoute>
        } />
        
        <Route path="/math-game/play" element={
          <ProtectedRoute>
            <MathGamePlay />
          </ProtectedRoute>
        } />

        <Route path="/math-game/lab" element={
          <ProtectedRoute>
            <MathLab />
          </ProtectedRoute>
        } />
        
        <Route path="/art" element={
          <ProtectedRoute>
            <Art />
          </ProtectedRoute>
        } />
        
        <Route path="/journal" element={
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        } />
        
        <Route path="/wordie" element={
          <ProtectedRoute>
            <Wordie />
          </ProtectedRoute>
        } />
        
        <Route path="/memory-game" element={
          <ProtectedRoute>
            <MemoryGame />
          </ProtectedRoute>
        } />
        
        <Route path="/typing-game" element={
          <ProtectedRoute>
            <TypingGame />
          </ProtectedRoute>
        } />
        
        <Route path="/stories" element={
          <ProtectedRoute>
            <Stories />
          </ProtectedRoute>
        } />
        
        <Route path="/simulator" element={
          <ProtectedRoute>
            <Simulator />
          </ProtectedRoute>
        } />
        
        <Route path="/science" element={
          <ProtectedRoute>
            <Science />
          </ProtectedRoute>
        } />
        
        {/* Dictionary lives inside Wordie now — this keeps old links working */}
        <Route path="/dictionary" element={
          <ProtectedRoute>
            <DictionaryRedirect />
          </ProtectedRoute>
        } />

        <Route path="/vocabulary" element={
          <ProtectedRoute>
            <Vocabulary />
          </ProtectedRoute>
        } />

        <Route path="/language" element={
          <ProtectedRoute>
            <Language />
          </ProtectedRoute>
        } />
        
        <Route path="/achievements" element={
          <ProtectedRoute>
            <Achievements />
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={<Navigate to="/account" replace />} />

        <Route path="/account" element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        } />
        
        {/* Catch all other routes */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
